import { Building2, Cpu, Factory, Network, ShieldAlert, ShieldCheck, Truck, Users } from 'lucide-react';

/*
 * Content for the Manufacturing scenario walkthrough, drawn from the
 * `manufacturing` industry demo pack in the BOSSS repo.
 *
 * Godwit Precision Industries is a composite demo organization, not a real
 * customer. Every `artifact` string is a verbatim record title from the pack,
 * read from the domain YAML rather than from ORG.md's storyline contract.
 *
 * The framing rules this content must satisfy are enforced by scenarios.test.js.
 */

export const godwitScenario = {
  org: {
    name: 'Godwit Precision Industries',
    subtitle: 'Five situations a manufacturer actually faces, on the plant floor and inside the CUI enclave.',
    framingNote: 'a composite organization in the BOSSS demo environment',
    profile: [
      { icon: Users, text: '~1,200 employees' },
      { icon: Factory, text: '3 plants + engineering campus' },
      { icon: ShieldCheck, text: 'DoD prime contract, CUI enclave' },
      { icon: Building2, text: 'Cedarbrook Tool & Die acquisition' },
    ],
  },
  scenarios: [
    {
      id: 'production-ransomware',
      number: 1,
      title: 'Ransomware Halts Production',
      tagline: 'The MES encrypts, and the CNC cells stop for want of work instructions',
      icon: ShieldAlert,
      frameworks: ['NIST CSF 2.0', 'IEC 62443', 'NIST 800-171'],
      situation: {
        narrative: 'A phishing email impersonating vendor support compromises a corporate workstation. Credentials '
          + 'are dumped, and the attacker pivots from IT into OT — there is no segmentation at Purdue Level 3 to '
          + 'stop it. Ransomware encrypts the Wrenfield MES and the process historian at the Rochester plant. '
          + 'The CNC cell fleet goes offline because it has no work instructions to run.',
        cast: [
          { name: 'Desmond Achebe', role: 'Chief Information Security Officer' },
          { name: 'Yolanda Prieto', role: 'Chief Operating Officer' },
          { name: 'Roland Deschene', role: 'Plant Manager, Rochester' },
          { name: 'Kirsten Rovang', role: 'OT Security Engineer, IT/OT liaison' },
        ],
      },
      timing: [
        { at: 'Day 0', label: 'Vendor-support phish on a corporate workstation' },
        { at: 'Day 1', label: 'Credential dump, pivot into OT' },
        { at: 'Day 2', label: 'MES and historian encrypted' },
        { at: 'Same day', label: 'Rochester CNC cells offline' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Lateral movement into the OT engineering workstation is detected',
          artifact: 'Lateral movement onto Rochester OT engineering workstation',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Mass encryption of the production systems raises the flagship alert',
          artifact: 'Ransomware mass-encryption of Wrenfield MES and process historian',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Production impact is detected as its own signal, not inferred later',
          artifact: 'Rochester CNC cell fleet offline — no work instructions',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Escalated to a full investigation case',
          artifact: 'Ransomware Halts Production — Rochester',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record covering the encrypted production estate',
          artifact: 'Ransomware Encryption of the Wrenfield MES & Process Historian',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The architectural cause is recorded as its own finding',
          artifact: 'No IT/OT Segmentation at L3 Permitted the Ransomware Pivot',
        },
      ],
      enables: [
        'Production stoppage and security detection are the same record, so the plant manager and the CISO are reading one timeline rather than arguing about two.',
        'The finding names the architecture that allowed the pivot, which is what turns an incident into a segmentation programme instead of a cleanup.',
      ],
      impact: {
        entities: [
          { name: 'Godwit Machining — Rochester', detail: 'Commercial plant', location: 'Rochester, MN' },
          { name: 'Godwit Precision Industries', detail: 'Corporate IT', location: 'Kellerton, MN' },
        ],
        locations: [
          { name: 'Godwit Machining Plant — Rochester', detail: 'Manufacturing facility · production halted', location: 'Rochester, MN' },
          { name: 'Godwit HQ & Engineering Campus', detail: 'Headquarters · initial compromise', location: 'Kellerton, MN' },
          { name: 'Godwit DR / Colocation Facility', detail: 'Disaster Recovery Site', location: 'St. Cloud, MN' },
        ],
        applications: [
          { name: 'Wrenfield MES Production Instance', detail: 'Encrypted', location: 'Rochester, MN' },
          { name: 'Process historian', detail: 'Encrypted', location: 'Rochester, MN' },
          { name: 'Corporate Workstation — Supply Chain', detail: 'Initial compromise', location: 'Kellerton, MN' },
          { name: 'Backup Server — DR Colocation', detail: 'Restore source', location: 'St. Cloud, MN' },
        ],
        vendors: [
          { name: 'Wrenfield Manufacturing Systems', detail: 'MES platform', location: 'Milwaukee, WI' },
          { name: 'Marlstone Endpoint Security', detail: 'Endpoint detection', location: 'Boulder, CO' },
          { name: 'Halbrook Security Analytics', detail: 'SIEM', location: 'Bethesda, MD' },
          { name: 'Kettleridge Automation', detail: 'Robotics and controls', location: 'Cleveland, OH' },
        ],
        data: [
          { name: 'Work Instruction (Traveler)', detail: '~118,000 travelers/year', location: 'Rochester, MN' },
          { name: 'Production Order', detail: '~34,000 orders/year', location: 'Rochester, MN' },
          { name: 'Process Historian Tag Timeseries', detail: '~9,400 tags at ~1 Hz', location: 'Rochester, MN' },
          { name: 'Material Traceability Record', detail: '~2.4M lot/serial records', location: 'Rochester, MN' },
        ],
      },
    },
    {
      id: 'cmmc-assessment',
      number: 2,
      title: 'CMMC 2.0 Level 2 Assessment',
      tagline: 'A defense enclave, an MFA gap, and a departing engineer',
      icon: ShieldCheck,
      frameworks: ['CMMC 2.0 Level 2', 'NIST 800-171', 'ITAR'],
      situation: {
        narrative: 'Godwit Defense Systems is the assessed boundary for CMMC 2.0 Level 2 — a distinct entity so the '
          + 'programme scopes to the CUI enclave rather than the whole company. Two problems surface during '
          + 'preparation: multi-factor authentication is not enforced on every local enclave account, and a '
          + 'departing design engineer attempts a bulk CAD download from the PLM vault followed by a USB copy.',
        cast: [
          { name: 'Camille Duquette', role: 'GRC Analyst, CMMC programme lead' },
          { name: 'Gerald Pankratz', role: 'Facility Security Officer' },
          { name: 'Nadia Chukwuma', role: 'CUI Program Manager' },
          { name: 'Aisha Warsame', role: 'Insider Risk & Data Protection Analyst' },
        ],
      },
      timing: [
        { at: 'In flight', label: 'CMMC L2 assessment preparation' },
        { at: 'Detected', label: 'Bulk CAD download from the PLM vault' },
        { at: 'Same period', label: 'USB mass-storage copy attempt' },
      ],
      response: [
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Bulk drawing access from the CUI vault is flagged',
          artifact: 'Bulk CAD/CUI drawing download from Slatemark PLM vault',
        },
        {
          module: 'XDR',
          href: '/platform/xdr',
          action: 'Worked as an insider investigation inside the defense enclave',
          artifact: 'Insider CAD/CUI Exfiltration Attempt — Defense Enclave',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for the attempted exfiltration',
          artifact: 'Departing Engineer\'s CUI/CAD Bulk-Download & USB Exfiltration Attempt',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The control gap is recorded against the specific CMMC practice',
          artifact: 'CMMC IA.L2-3.5.3 — MFA Not Enforced for Every Local Enclave Account',
        },
        {
          module: 'IAM',
          href: '/platform/iam',
          action: 'Enclave accounts and their entitlements are governed separately from corporate identity',
          artifact: 'Defense Enclave Directory',
        },
      ],
      enables: [
        'The CUI enclave is its own entity, so the assessment scopes to the assessed boundary instead of dragging three commercial plants into it.',
        'A finding cites the CMMC practice identifier directly, which is what an assessor asks for rather than a paraphrase of it.',
      ],
      impact: {
        entities: [
          { name: 'Godwit Defense Systems', detail: 'CUI enclave — assessed boundary', location: 'Kellerton, MN' },
        ],
        locations: [
          { name: 'Godwit Defense Systems Facility', detail: 'Office · CUI enclave', location: 'Kellerton, MN' },
          { name: 'Godwit HQ & Engineering Campus', detail: 'Headquarters · engineering', location: 'Kellerton, MN' },
        ],
        applications: [
          { name: 'Slatemark PLM CAD Vault', detail: 'Bulk download source', location: 'Kellerton, MN' },
          { name: 'CUI File Server — Defense Enclave', location: 'Kellerton, MN' },
          { name: 'CUI Enclave Workstation Fleet', detail: 'MFA gap', location: 'Kellerton, MN' },
          { name: 'Defense Enclave Directory', location: 'Kellerton, MN' },
        ],
        vendors: [
          { name: 'Slatemark Design Systems', detail: 'PLM / CAD platform', location: 'Ann Arbor, MI' },
          { name: 'Quarrymark Compliance Group', detail: 'CMMC assessor', location: 'Huntsville, AL' },
          { name: 'Pelham Identity', detail: 'SSO / identity provider', location: 'Seattle, WA' },
        ],
        data: [
          { name: 'CUI Technical Drawing', detail: '~3,400 controlled drawings', location: 'Kellerton, MN' },
          { name: 'CUI Marking / Banner', detail: '~14,200 marked artifacts', location: 'Kellerton, MN' },
          { name: 'Export-Controlled Design Drawing', detail: '~640 drawings', location: 'Kellerton, MN' },
          { name: 'Defense Part Number', detail: '~1,150 part numbers', location: 'Kellerton, MN' },
        ],
      },
    },
    {
      id: 'ot-segmentation',
      number: 3,
      title: 'OT Segmentation Programme',
      tagline: 'Flat plant networks becoming Purdue zones and conduits',
      icon: Network,
      frameworks: ['IEC 62443', 'NIST CSF 2.0'],
      situation: {
        narrative: 'The Duluth fabrication plant runs flat from Purdue Level 1 to Level 3 with no industrial DMZ — '
          + 'the same class of weakness that let ransomware reach Rochester production. A segmentation programme '
          + 'runs across the plants to establish zones and conduits, with endpoint coverage on OT assets that '
          + 'were never in scope for corporate tooling.',
        cast: [
          { name: 'Sylvia Nkemdirim', role: 'OT Network Engineer, Purdue zone lead' },
          { name: 'Hector Balandran', role: 'Director, OT & Controls Engineering' },
          { name: 'Pavel Rutkowski', role: 'Controls Engineer, Duluth' },
          { name: 'Stefan Kubiak', role: 'Plant Manager, Duluth' },
        ],
      },
      timing: [
        { at: 'Assessed', label: 'Duluth network found flat L1 to L3' },
        { at: 'Programme', label: 'Zones and conduits designed per plant' },
        { at: 'Tracked', label: 'OT endpoint coverage gap worked to closure' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The flat plant network is recorded as a finding in its own right',
          artifact: 'Duluth Plant Network Flat from L1 to L3 — No IDMZ',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The IT/OT boundary gap links the programme back to the Rochester incident',
          artifact: 'No IT/OT Segmentation at L3 Permitted the Ransomware Pivot',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Detection coverage on OT assets is tracked as a distinct gap',
          artifact: 'OT endpoint detection coverage gap',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'PLCs, HMIs and historians are inventory with zones, not undocumented plant equipment',
          artifact: 'OT asset inventory across three plants',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Segmentation work lands as approved change with plant sign-off',
          artifact: 'OT segmentation change programme',
        },
      ],
      enables: [
        'A control weakness at one plant and the incident it enabled at another reference each other, so the programme is justified by evidence rather than by analogy.',
        'OT assets carry their Purdue level in the same inventory as corporate IT, which is what makes a zone boundary checkable instead of aspirational.',
      ],
      impact: {
        entities: [
          { name: 'Godwit Fabrication — Duluth', detail: 'Commercial plant', location: 'Duluth, MN' },
          { name: 'Godwit Machining — Rochester', detail: 'Commercial plant', location: 'Rochester, MN' },
        ],
        locations: [
          { name: 'Godwit Fabrication Plant — Duluth', detail: 'Manufacturing facility · flat network', location: 'Duluth, MN' },
          { name: 'Godwit Machining Plant — Rochester', detail: 'Manufacturing facility', location: 'Rochester, MN' },
          { name: 'Cedarbrook Tool & Die Plant', detail: 'Manufacturing facility', location: 'Eau Claire, WI' },
        ],
        applications: [
          { name: 'Wrenfield MES Production Instance', location: 'Rochester, MN' },
          { name: 'Process historian', location: 'Rochester, MN' },
          { name: 'Plant HMI and PLC estate', detail: 'Purdue L1–L2', location: 'Duluth, MN' },
        ],
        vendors: [
          { name: 'Thornwick Industrial Networks', detail: 'OT networking', location: 'Richardson, TX' },
          { name: 'Kettleridge Automation', detail: 'Robotics and controls', location: 'Cleveland, OH' },
          { name: 'Marlstone Endpoint Security', detail: 'Endpoint detection', location: 'Boulder, CO' },
        ],
        data: [
          { name: 'PLC Control Program', detail: '~180 programs across 3 sites', location: 'Duluth, MN' },
          { name: 'HMI Alarm & Event Log', detail: '~1.1M entries/year', location: 'Duluth, MN' },
          { name: 'Process Historian Tag Timeseries', detail: '~9,400 tags at ~1 Hz', location: 'Rochester, MN' },
        ],
      },
    },
    {
      id: 'supply-chain',
      number: 4,
      title: 'Supply-Chain and SBOM Exposure',
      tagline: 'A component supplier is breached, and the firmware is already installed',
      icon: Truck,
      frameworks: ['NIST 800-171', 'NIST CSF 2.0', 'IEC 62443'],
      situation: {
        narrative: 'Vandercroft Components, a component supplier, discloses a breach with firmware supply-chain '
          + 'exposure. The question is not whether the supplier is secure but which Godwit equipment is already '
          + 'running the affected firmware — a question answerable only if component SBOMs are inventory rather '
          + 'than paperwork.',
        cast: [
          { name: 'Rafael Ontiveros', role: 'VP Supply Chain' },
          { name: 'Loretta Vanhouten', role: 'Director, Contracts & Compliance' },
          { name: 'Kirsten Rovang', role: 'OT Security Engineer' },
          { name: 'Pavel Rutkowski', role: 'Controls Engineer, Duluth' },
        ],
      },
      timing: [
        { at: 'Disclosed', label: 'Supplier breach notification received' },
        { at: 'Assessed', label: 'Affected firmware traced through SBOMs' },
        { at: 'Tracked', label: 'Supplier oversight gap opened' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for the supplier breach and firmware exposure',
          artifact: 'Component-Supplier Breach & Firmware Supply-Chain Exposure (Vandercroft)',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The oversight weakness the breach revealed becomes its own finding',
          artifact: 'Supplier Security Oversight Gap Surfaced by the Vandercroft Breach',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Component SBOMs make the affected equipment answerable rather than guessed',
          artifact: 'Component firmware SBOM inventory',
        },
        {
          module: 'Business',
          href: '/platform/business',
          action: 'Supplier contracts and purchase records establish what was bought and under what terms',
          artifact: 'Supplier purchase order and pricing records',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Firmware remediation is scheduled against affected equipment',
          artifact: 'Supplier firmware remediation change',
        },
      ],
      enables: [
        'The question "which of our machines runs this firmware" is answered from inventory rather than from a supplier phone call.',
        'Supplier risk, the equipment it affects and the contract that governs it are one chain instead of three departments.',
      ],
      impact: {
        entities: [
          { name: 'Godwit Precision Industries', detail: 'Supply chain', location: 'Kellerton, MN' },
          { name: 'Vandercroft Components', detail: 'Supplier — breached', location: 'Grand Rapids, MI' },
        ],
        locations: [
          { name: 'Godwit Fabrication Plant — Duluth', detail: 'Manufacturing facility · affected equipment', location: 'Duluth, MN' },
          { name: 'Godwit HQ & Engineering Campus', detail: 'Headquarters · supply chain', location: 'Kellerton, MN' },
          { name: 'Vandercroft Components Headquarters', detail: 'Supplier site — breached', location: 'Grand Rapids, MI' },
        ],
        applications: [
          { name: 'Godwit Supplier Portal', location: 'Kellerton, MN' },
          { name: 'Godwit ERP Production', detail: 'Purchase and vendor master', location: 'Kellerton, MN' },
          { name: 'Plant HMI and PLC estate', detail: 'Affected firmware', location: 'Duluth, MN' },
        ],
        vendors: [
          { name: 'Vandercroft Components', detail: 'Breached — component supplier', location: 'Grand Rapids, MI' },
          { name: 'Sablecreek Precision Machining', detail: 'Contract machining supplier', location: 'Cedar Rapids, IA' },
          { name: 'Thornwick Industrial Networks', detail: 'OT networking', location: 'Richardson, TX' },
        ],
        data: [
          { name: 'Component Firmware SBOM', detail: '~140 SBOMs', location: 'Kellerton, MN' },
          { name: 'Supplier Purchase Order & Pricing', detail: '~9,600 orders/year', location: 'Kellerton, MN' },
          { name: 'PLC Control Program', detail: '~180 programs across 3 sites', location: 'Duluth, MN' },
        ],
      },
    },
    {
      id: 'acquisition-integration',
      number: 5,
      title: 'Acquisition Integration Risk',
      tagline: 'Eighteen months after closing, discovery finds machines nobody recorded',
      icon: Cpu,
      frameworks: ['NIST CSF 2.0', 'IEC 62443'],
      situation: {
        narrative: 'Cedarbrook Tool & Die was acquired eighteen months ago and still runs a flat legacy network. '
          + 'Asset discovery finds devices that do not appear in any record — including unpatched Windows 7 HMIs '
          + 'running production. The integration risk is not theoretical: it is a plant whose inventory nobody '
          + 'can vouch for.',
        cast: [
          { name: 'Marisol Tavarez', role: 'Plant Manager, Cedarbrook Tool & Die' },
          { name: 'Harold Pettersen', role: 'Security Engineer' },
          { name: 'Desmond Achebe', role: 'Chief Information Security Officer' },
        ],
      },
      timing: [
        { at: '−18 months', label: 'Acquisition closed' },
        { at: 'Discovery', label: 'Undocumented devices found on the network' },
        { at: 'Tracked', label: 'Legacy HMI exposure worked to remediation' },
      ],
      response: [
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Formal incident record for what discovery turned up',
          artifact: 'Undocumented Devices Found on the Cedarbrook Network During Discovery',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'The inventory mismatch is a finding, not a data-cleanup task',
          artifact: 'Cedarbrook Asset Records Do Not Match Discovery',
        },
        {
          module: 'GRC',
          href: '/platform/grc',
          action: 'Legacy operating systems running production are recorded explicitly',
          artifact: 'Unpatched Windows 7 HMIs on the Cedarbrook Flat Network',
        },
        {
          module: 'CMDB',
          href: '/platform/cmdb',
          action: 'Discovery reconciles the acquired estate against the record',
          artifact: 'Cedarbrook plant asset inventory',
        },
        {
          module: 'Service Desk',
          href: '/platform/service-desk',
          action: 'Remediation is scheduled around production windows',
          artifact: 'Cedarbrook HMI remediation change',
        },
      ],
      enables: [
        'An acquired plant is reconciled against discovery rather than trusted, which is the difference between an asset register and an inventory.',
        'Integration risk carries the same finding and remediation machinery as everything else, so it does not live in a separate post-merger spreadsheet.',
      ],
      impact: {
        entities: [
          { name: 'Cedarbrook Tool & Die', detail: 'Acquired subsidiary', location: 'Eau Claire, WI' },
          { name: 'Godwit Precision Industries', detail: 'Parent', location: 'Kellerton, MN' },
        ],
        locations: [
          { name: 'Cedarbrook Tool & Die Plant', detail: 'Manufacturing facility · flat legacy network', location: 'Eau Claire, WI' },
          { name: 'Godwit HQ & Engineering Campus', detail: 'Headquarters · integration programme', location: 'Kellerton, MN' },
        ],
        applications: [
          { name: 'Legacy Windows 7 HMIs', detail: 'Unpatched, running production', location: 'Eau Claire, WI' },
          { name: 'Cedarbrook plant network', detail: 'Flat, unsegmented', location: 'Eau Claire, WI' },
          { name: 'Active Directory', detail: 'Integration target', location: 'Kellerton, MN' },
        ],
        vendors: [
          { name: 'Coldbrook Managed IT', detail: 'Managed service provider', location: 'Wausau, WI' },
          { name: 'Marlstone Endpoint Security', detail: 'Endpoint detection', location: 'Boulder, CO' },
        ],
        data: [
          { name: 'Tool & Die Design / Process Know-How', detail: '~4,700 designs', location: 'Eau Claire, WI' },
          { name: 'HMI Alarm & Event Log', detail: '~1.1M entries/year', location: 'Eau Claire, WI' },
          { name: 'Commercial Drawing', detail: '~31,000 released drawings', location: 'Kellerton, MN' },
        ],
      },
    },
  ],
};
